/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */

import React, { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { closeModal, closeSecondaryModal, openModal, openSecondaryModal } from 'helpers/actions'
import { useAppDispatch } from 'helpers/store'
import { useTypedSelector } from 'helpers/reducers'
import { OffersAction } from 'store/offers/OffersAction'
import OpportunitiesAction from 'store/opportunities/OpportunitiesAction'
import { toast } from 'utils/toastUtils'
import { getCompanyDisplayName } from 'utils/companyUtils'
import { useLeadActions } from 'utils/leads/leadsUtils'
import EditOfferModal from 'components/Offers/EditOfferModal'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import { UiButtonStyle } from 'components/Ui/UiButton'
import { Offer } from 'types/entities/Offer'
import { Lead } from 'types/entities/Lead'
import { LeadCategory } from 'types/enums/LeadCategory'
import { ModalSize } from 'types/enums/ModalSize'
import { EditLeadModalTab } from 'types/enums/specific/EditLeadModalTab'

export const useLeadOffersActions = (leadId: number) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const { openEditLeadModal } = useLeadActions()
    const openEditLeadModalRef = useRef(openEditLeadModal)
    const isSecondaryModalOpen = useTypedSelector((state) => state.ui.secondaryModal.isOpen)
    const isSecondaryModalOpenRef = useRef(false)

    React.useEffect(() => {
        isSecondaryModalOpenRef.current = isSecondaryModalOpen
    }, [isSecondaryModalOpen])

    React.useEffect(() => {
        openEditLeadModalRef.current = openEditLeadModal
    }, [openEditLeadModal])

    const refreshOpportunityOffers = useCallback(async () => {
        await dispatch(OpportunitiesAction.fetchLeadById({ id: leadId, category: LeadCategory.Opportunity } as Lead))
    }, [dispatch, leadId])

    const openOfferDetail = useCallback(
        (offer: Offer) => {
            const handleClose = () => {
                if (isSecondaryModalOpenRef.current) return
                dispatch(closeModal())
                openEditLeadModalRef.current({ id: leadId } as Lead, () => {}, EditLeadModalTab.Offers)
            }
            dispatch(
                openModal(
                    <EditOfferModal offerId={offer.id} onClose={handleClose} />,
                    { modalSize: ModalSize.RoundedFullscreen, closeModal: handleClose },
                    t('Offer Detail')
                )
            )
        },
        [dispatch, leadId, t]
    )

    const deleteOfferHandler = useCallback(
        (offer: Offer) => {
            if (!offer.id) return
            dispatch(
                openSecondaryModal(
                    <UiAlertConfirmationModal
                        title={t('Delete Offer')}
                        message={
                            <div className={'flex flex-col gap-2'}>
                                <span>ID: {offer.id}</span>
                                <span>
                                    {t('Customer')}: {getCompanyDisplayName(offer.company)}
                                </span>
                                <div className={'border-gray-300 border-t pt-3 font-bold'}>
                                    {t('Are you sure you want to delete this offer?')}
                                </div>
                            </div>
                        }
                        onConfirm={() => {
                            dispatch(OffersAction.fetchDeleteOffer(offer.id))
                                .then(async () => {
                                    await refreshOpportunityOffers()
                                    toast.success(t('Offer deleted'))
                                    dispatch(closeSecondaryModal())
                                })
                                .catch(() => {
                                    toast.error(t('Failed to delete offer'))
                                    dispatch(closeSecondaryModal())
                                })
                        }}
                        onCancel={() => dispatch(closeSecondaryModal())}
                        confirmStyle={UiButtonStyle.Primary}
                    />,
                    { modalSize: ModalSize.Small },
                    t('Confirm')
                )
            )
        },
        [dispatch, refreshOpportunityOffers, t]
    )

    return { openOfferDetail, deleteOfferHandler, refreshOpportunityOffers }
}
